import { Share2 } from "lucide-react";
import { toast } from "sonner";

/**
 * A simple share button next to a tool's title -- lets someone send
 * this specific tool to a friend/colleague with one tap, rather than
 * needing to copy the address bar URL manually. Uses the real Web
 * Share API where the browser supports it (mobile Safari/Chrome --
 * opens the device's native share sheet, straight to Messages/WhatsApp/
 * etc.), falling back to copying the link to the clipboard with a
 * toast confirmation on desktop browsers that don't support it.
 */
export function ShareButton({ title, className = "" }: { title: string; className?: string }) {
  async function handleShare(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();

    const url = window.location.href;
    const shareData = { title: `${title} | FreeDailyPro`, url };

    if (navigator.share && navigator.canShare?.(shareData)) {
      try {
        await navigator.share(shareData);
      } catch {
        // User canceled the share sheet -- not an error, do nothing.
      }
      return;
    }

    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Couldn't copy the link — try copying it from the address bar.");
    }
  }

  return (
    <button
      onClick={handleShare}
      title="Share this tool"
      aria-label="Share this tool"
      className={`inline-flex items-center justify-center min-h-11 min-w-11 max-sm:-mx-1.5 sm:min-h-0 sm:min-w-0 shrink-0 transition-colors text-muted-foreground hover:text-foreground ${className}`}
    >
      <Share2 className="h-4 w-4" />
    </button>
  );
}
